import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Container, Row, Col, Card, Table, Form, Button, Pagination, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import APIserver from "../../../../api/axiosConfig";

/**
 * ReporteHistorial (v2)
 * - Filtro de fechas (mes actual por defecto)
 * - Paginación servidor (page/limit)
 * - Selector de filas por página
 * - Exportar a Excel del rango (sin paginación)
 * - Columnas: Referencia, Producto, Campo Modificado, Valor Anterior, Valor Nuevo, Fecha Modificación
 *
 * Backend esperado:
 *   GET  /api/reportesHistorial/historial-cambios
 *   GET  /api/reportesHistorial/historial-cambios/export (xlsx)
 */

/** Utils **/
const todayISO = () => new Date().toISOString().split("T")[0];
const startOfThisMonthISO = () => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), 1);
  return d.toISOString().split("T")[0];
};
const endOfThisMonthISO = () => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return d.toISOString().split("T")[0];
};
//const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString("es-CO", { timeZone: "UTC" }) : "");

function PaginationBar({ currentPage, totalPages, onPageChange, maxVisible = 5 }) {
  const items = useMemo(() => {
    const arr = [];
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    if (start > 1) arr.push(<Pagination.First key="first" onClick={() => onPageChange(1)} />);
    if (currentPage > 1) arr.push(<Pagination.Prev key="prev" onClick={() => onPageChange(currentPage - 1)} />);
    for (let p = start; p <= end; p++) arr.push(<Pagination.Item key={p} active={p === currentPage} onClick={() => onPageChange(p)}>{p}</Pagination.Item>);
    if (currentPage < totalPages) arr.push(<Pagination.Next key="next" onClick={() => onPageChange(currentPage + 1)} />);
    if (end < totalPages) arr.push(<Pagination.Last key="last" onClick={() => onPageChange(totalPages)} />);
    return arr;
  }, [currentPage, totalPages, onPageChange, maxVisible]);
  return <Pagination className="mb-0">{items}</Pagination>;
}

const ReporteHistorial = () => {
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFechaInicio(startOfThisMonthISO());
    setFechaFin(endOfThisMonthISO());
  }, []);

  const onChangeDesde = (value) => {
    const sel = new Date(value);
    const max = new Date(fechaFin || todayISO());
    if (sel <= max) { setFechaInicio(value); setCurrentPage(1); }
  };
  const onChangeHasta = (value) => {
    const sel = new Date(value);
    const min = new Date(fechaInicio);
    const now = new Date();
    if (sel >= min && sel <= now) { setFechaFin(value); setCurrentPage(1); }
  };

  const manejarExportar = useCallback(async () => {
    try {
      const res = await APIserver.get('/api/reportesHistorial/historial-cambios/export', {
        params: { fechaInicio, fechaFin },
        responseType: 'blob',

      });
      if (res.status === 204 || !res.data || res.data.size === 0) {
        await Swal.fire({ title: 'Sin datos', text: 'No hay registros en el rango seleccionado.', icon: 'info', showConfirmButton: false, timer: 2000, timerProgressBar: true, iconColor: '#2fb380', confirmButtonColor: '#2c4cc4' });
        return;
      }
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url; a.download = `HistorialCambios_${fechaInicio}_${fechaFin}.xlsx`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      } finally { window.URL.revokeObjectURL(url); }
    } catch (e) {
      console.error('Export error', e);
      await Swal.fire({ title: 'Error', text: 'No se pudo exportar.', icon: 'error', confirmButtonColor: '#2c4cc4' });
    }
  }, [fechaInicio, fechaFin]);

  const fetchRows = useCallback(async () => {
    if (!fechaInicio || !fechaFin) return;
    setLoading(true);
    try {
      const { data } = await APIserver.get('/api/reportesHistorial/historial-cambios', { params: { fechaInicio, fechaFin, page: currentPage, limit: rowsPerPage } });
      const results = Array.isArray(data?.results) ? data.results : [];
      setRows(results);
      setTotalPages(Math.max(1, Number(data?.totalPages ?? 1)));
      setTotalCount(Number(data?.totalCount ?? results.length));
    } catch (e) {
      console.error('Error cargando historial:', e);
      setRows([]); setTotalPages(1); setTotalCount(0);
      Swal.fire({ title: 'Error', text: 'No se pudo cargar el historial de cambios.', icon: 'error', confirmButtonColor: '#2c4cc4' });
    } finally { setLoading(false); }
  }, [fechaInicio, fechaFin, currentPage, rowsPerPage]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  return (
    <Container fluid className="py-3">
      {/* Filtros */}
      <div className="d-flex flex-column flex-md-row align-items-center align-items-md-end justify-content-center mb-3 text-center">
        <div className="d-flex flex-column me-md-3 mb-3 mb-md-0">
          <Form.Label>Desde</Form.Label>
          <Form.Control type="date" value={fechaInicio} onChange={(e) => onChangeDesde(e.target.value)} max={todayISO()} style={{ minWidth: 200 }} />
        </div>
        <div className="d-flex flex-column me-md-3 mb-3 mb-md-0">
          <Form.Label>Hasta</Form.Label>
          <Form.Control type="date" value={fechaFin} onChange={(e) => onChangeHasta(e.target.value)} max={todayISO()} min={fechaInicio || undefined} style={{ minWidth: 200 }} />
        </div>
        <div className="d-flex align-items-center justify-content-end mt-3 mt-md-0">
          <Button variant="danger" onClick={manejarExportar}>Exportar</Button>
        </div>
      </div>

      <Row className="mb-3 text-center">
        <Col md={3}>
          <Card bg="light" className="text-dark">
            <Card.Body>
              <Card.Title>Registros</Card.Title>
              <h4 className="mb-0">{loading ? <Spinner size="sm" animation="border" /> : totalCount}</h4>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div style={{ boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.1)" }}>
        <Table responsive className="text-center align-middle border">
          <thead>
            <tr>
              <th colSpan={6}>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2">
                    <Form.Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setCurrentPage(1); }} style={{ width: 100 }}>
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </Form.Select>
                  </div>
                </div>
              </th>
            </tr>
            <tr>
              <th>Referencia</th>
              <th>Producto</th>
              <th>Campo Modificado</th>
              <th>Valor Anterior</th>
              <th>Valor Nuevo</th>
              <th>Valor Ingreso</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="py-4"><Spinner animation="border" role="status" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="py-4 text-muted">Sin registros para el rango seleccionado.</td></tr>
            ) : (
              // desde aqui tocó Juan
              rows.map((r) => {
                // Verifica si ambos valores son numéricos
                const numAnterior = Number(r.valorAnterior);
                const numNuevo = Number(r.valorNuevo);
                const esNumerico = !isNaN(numAnterior) && !isNaN(numNuevo);

                return (
                  <tr key={`${r.referencia}-${r.fechaModificacion}-${r.campoModificado}`}>
                    <td className="text-decoration-none">{r.referencia}</td>
                    <td>{r.nomProducto ?? '—'}</td>
                    <td>{r.campoModificado}</td>
                    <td className="text-truncate" style={{ maxWidth: 240 }} title={r.valorAnterior}>{r.valorAnterior}</td>
                    <td className="text-truncate" style={{ maxWidth: 240 }} title={r.valorNuevo}>{r.valorNuevo}</td>
                    <td>
                      {esNumerico ? numNuevo - numAnterior : '-'}
                    </td>
                  </tr>
                );
              })
              // Fin de la aportacion de Juan
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={6}>
                <div className="d-flex justify-content-center align-items-center">
                  <PaginationBar currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                </div>
              </td>
            </tr>
          </tfoot>
        </Table>
      </div>
    </Container>
  );
};

export default ReporteHistorial;
